import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Breadcrumb,
    Button,
    Dropdown,
    Grid,
    Icon,
    Input,
    Label,
    Menu,
    Message,
    Modal,
    Popup,
    Table
} from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { CupomGerenciadorListAction, CupomGerenciadorVendedoresListAction, InsertCupomDescontoAction } from '../../actions/CupomAction';

const Page = styled.div`
    padding: 1em 0;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1em;
    flex-wrap: wrap;
    padding: 1em 0;
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: flex-end;
    gap: .75em;
    flex-wrap: wrap;
`;

const FilterField = styled.label`
    display: flex;
    flex-direction: column;
    gap: .35em;
    color: #1884af;
    font-weight: 700;
    min-width: 220px;
`;

const CodParcField = styled(FilterField)`
    min-width: 160px;
`;

const TableWrap = styled.div`
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid #d7e8ef;
    border-radius: 8px;
    background: #fff;
`;

const StyledTable = styled(Table)`
    &&& {
        margin: 0;
        width: 100%;
        table-layout: fixed;
    }

    &&& thead th {
        background: #f3f9fb;
        color: #24424f;
        font-size: .76em;
        padding: .55em .35em !important;
        text-align: center;
        white-space: normal;
        line-height: 1.15;
    }

    &&& tbody td {
        font-size: .78em;
        padding: .45em .35em !important;
        vertical-align: middle;
        line-height: 1.2;
        overflow-wrap: anywhere;
        word-break: normal;
    }
`;

const Summary = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1em;
    flex: 1 1 360px;
    min-width: 300px;
    color: #48616b;
    font-weight: 600;
`;

const SkeletonBlock = styled.span`
    display: inline-block;
    width: ${props => props.width || '80px'};
    height: 12px;
    border-radius: 8px;
    background: linear-gradient(90deg, #edf1f4 0%, #f8fbfc 45%, #edf1f4 90%);
    background-size: 220% 100%;
    animation: cupomSkeleton 1.15s ease-in-out infinite;

    @keyframes cupomSkeleton {
        0% { background-position: 120% 0; }
        100% { background-position: -120% 0; }
    }
`;

const cupomColumns = [
    { key: 'supervisor', label: 'Supervisor', width: '13%' },
    { key: 'vendedor', label: 'Vendedor', width: '14%' },
    { key: 'parceiro', label: 'Parceiro', width: '25%' },
    { key: 'cupom', label: 'Cupom', width: '14%' },
    { key: 'desconto', label: 'Desconto', textAlign: 'center', width: '7%' },
    { key: 'vigencia', label: 'Vigencia', textAlign: 'center', width: '12%' },
    { key: 'nfs_emitidas', label: 'NFs Emitidas', textAlign: 'center', width: '5%' },
    { key: 'vendas', label: 'Vendas', textAlign: 'center', width: '6%' },
    { key: 'acoes', label: '', textAlign: 'center', width: '4%' }
];

const cupomAtivoColumns = [
    { key: 'supervisor', label: 'Supervisor', width: '11%' },
    { key: 'vendedor', label: 'Vendedor', width: '12%' },
    { key: 'parceiro_ativo', label: 'Parceiro', width: '24%' },
    { key: 'cupom', label: 'Cupom', width: '12%' },
    { key: 'desconto', label: 'Desconto', textAlign: 'center', width: '7%' },
    { key: 'vigencia', label: 'Vigencia', textAlign: 'center', width: '11%' },
    { key: 'nfs_emitidas', label: 'NFs Emitidas', textAlign: 'center', width: '5%' },
    { key: 'qtd_orcamentos', label: 'Qtde. orçamentos', textAlign: 'center', width: '8%' },
    { key: 'vendas', label: 'Vendas', textAlign: 'center', width: '6%' },
    { key: 'acoes', label: '', textAlign: 'center', width: '4%' }
];

const elegivelColumns = [
    { key: 'supervisor', label: 'Supervisor', width: '8%' },
    { key: 'vendedor', label: 'Vendedor', width: '8%' },
    { key: 'parceiro_elegivel', label: 'Parceiro', width: '13%' },
    { key: 'dtultvisita', label: 'Última visita', textAlign: 'center', width: '7%' },
    { key: 'compraconcorrente', label: 'Compra concorrente', textAlign: 'center', width: '7%' },
    { key: 'classificicms', label: 'Revenda?', textAlign: 'center', width: '6%' },
    { key: 'teve_cupom', label: 'Teve cupom', textAlign: 'center', width: '6%' },
    { key: 'ultima_compra', label: 'Última compra', textAlign: 'center', width: '7%' },
    { key: '3_meses_sem_comprar', label: '3 meses sem comprar', textAlign: 'center', width: '7%' },
    { key: 'comprou_mais_10k_4anos', label: '+R$10k em 4 anos?', textAlign: 'center', width: '7%' },
    { key: 'pct_media_anual', label: 'Variação anual', textAlign: 'center', width: '6%' },
    { key: 'queda_media_anual', label: 'Queda na média?', textAlign: 'center', width: '6%' },
    { key: 'queda_media_anual_50', label: 'Queda maior que 50%?', textAlign: 'center', width: '7%' },
    { key: 'acoes_elegivel', label: '', textAlign: 'center', width: '5%' }
];

const statusByIndex = ['ATIVO', 'INATIVO', 'ELEGIVEL', 'INELEGIVEL'];
const statusLabels = {
    ATIVO: 'Ativos',
    INATIVO: 'Inativos',
    ELEGIVEL: 'Elegíveis',
    INELEGIVEL: 'Inelegíveis'
};
const cupomSimulado = {
    tipo: 'Cupom de reativação',
    desconto: '17,99%'
};
const cupomAberturaSimulado = {
    tipo: 'Cupom de abertura',
    desconto: '19,99%'
};
const criteriosCupomBase = [
    'Revenda',
    'Visitado pelo externo em 1 semana',
    'Formulário de visita respondido indicando compra com concorrente ou direto na fábrica',
    'Nunca ter tido cupom'
];
const criteriosCupomAbertura = [
    'Até 5 dentro do mês atual',
    'Para clientes/coligados que nunca compraram'
];
const criteriosCupomReativacao = [
    'Até 5 dentro do mês atual',
    'Cliente sem comprar há 3 meses',
    'Com queda na média anual de faturamento',
    'Ou já comprou mais de R$ 10 mil nos últimos 4 anos com queda acima de 50% na média anual de faturamento'
];

function formatDate(value) {
    if (!value) {
        return '-';
    }

    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
        return value;
    }

    return dateValue.toLocaleDateString('pt-BR');
}

function getFieldValue(item, key) {
    if (!item || !key) {
        return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(item, key)) {
        return item[key];
    }

    const upperKey = key.toUpperCase();
    if (Object.prototype.hasOwnProperty.call(item, upperKey)) {
        return item[upperKey];
    }

    const lowerKey = key.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(item, lowerKey)) {
        return item[lowerKey];
    }

    const foundKey = Object.keys(item).find((itemKey) => itemKey.toLowerCase() === lowerKey);
    return foundKey ? item[foundKey] : undefined;
}

function getCupomOfertaParceiro(parceiro) {
    const ultimaCompra = getFieldValue(parceiro, 'ultima_compra');
    const ultimaCompraNormalizada = String(ultimaCompra || '').trim().toLowerCase();
    const possuiUltimaCompra = ultimaCompra !== null
        && ultimaCompra !== undefined
        && ultimaCompraNormalizada !== ''
        && ultimaCompraNormalizada !== '-'
        && ultimaCompraNormalizada !== 'null';

    return possuiUltimaCompra
        ? { ...cupomSimulado, opcao: 1 }
        : { ...cupomAberturaSimulado, opcao: 2 };
}

function getMotivosInelegivel(parceiro) {
    const motivo = getFieldValue(parceiro, 'motivo_inelegivel');

    if (!motivo) {
        return ['Nenhum motivo informado para este parceiro.'];
    }

    return String(motivo)
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean);
}

function formatValue(key, value, item) {
    if (key === 'acoes' || key === 'acoes_elegivel') {
        return '';
    }

    if (key === 'parceiro') {
        const itemCodparc = getFieldValue(item, 'codparc');
        const itemParceiro = getFieldValue(item, 'parceiro');
        const codparc = itemCodparc !== null && itemCodparc !== undefined ? itemCodparc : '';
        const parceiro = itemParceiro || '';

        if (!codparc && !parceiro) {
            return '-';
        }

        return `${codparc} - ${parceiro}`.trim();
    }

    if (key === 'parceiro_ativo') {
        const itemCodparc = getFieldValue(item, 'codparc');
        const itemParceiro = getFieldValue(item, 'parceiro');
        const itemCidade = getFieldValue(item, 'cidade');
        const codparc = itemCodparc !== null && itemCodparc !== undefined ? itemCodparc : '';
        const parceiro = itemParceiro || '';
        const cidade = itemCidade !== null && itemCidade !== undefined ? String(itemCidade).trim() : '';

        if (!codparc && !parceiro) {
            return '-';
        }

        const parceiroFormatado = `${codparc} - ${parceiro}`.trim();
        return cidade ? `${parceiroFormatado} (${cidade})` : parceiroFormatado;
    }

    if (key === 'parceiro_elegivel') {
        const itemCodparc = getFieldValue(item, 'codparc');
        const itemNomeparc = getFieldValue(item, 'nomeparc');
        const codparc = itemCodparc !== null && itemCodparc !== undefined ? itemCodparc : '';
        const nomeparc = itemNomeparc || '';

        if (!codparc && !nomeparc) {
            return '-';
        }

        return `${codparc} - ${nomeparc}`.trim();
    }

    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (key === 'desconto') {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? `${numberValue.toFixed(2).replace('.', ',')}%` : value;
    }

    if (key === 'vendas') {
        const numberValue = Number(value);
        return Number.isFinite(numberValue)
            ? numberValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            : value;
    }

    if (key === 'dtultvisita' || key === 'ultima_compra') {
        return formatDate(value);
    }

    if (
        key === 'teve_cupom'
        || key === 'compraconcorrente'
        || key === '3_meses_sem_comprar'
        || key === 'comprou_mais_10k_4anos'
        || key === 'queda_media_anual'
        || key === 'queda_media_anual_50'
    ) {
        return String(value).toUpperCase() === 'S' ? 'Sim' : 'Não';
    }

    if (key === 'classificicms') {
        return String(value).toUpperCase() === 'R' ? 'Sim' : 'Não';
    }

    if (key === 'pct_media_anual') {
        const numberValue = Number(value);
        return Number.isFinite(numberValue)
            ? `${numberValue.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
            : value;
    }

    return value;
}

function CupomGerenciador(props) {
    const {
        userid,
        codvendUsuario,
        tipvend,
        menu,
        vendedores,
        vendedoresLoading,
        cuponsByStatus,
        loadingByStatus,
        erroByStatus,
        history,
        CupomGerenciadorListAction,
        CupomGerenciadorVendedoresListAction,
        InsertCupomDescontoAction
    } = props;
    const [activeIndex, setActiveIndex] = useState(0);
    const [vendedorSelecionado, setVendedorSelecionado] = useState('');
    const [codparcInput, setCodparcInput] = useState('');
    const [codparcFiltro, setCodparcFiltro] = useState('');
    const [codparcInelegivel, setCodparcInelegivel] = useState('');
    const [codparcInelegivelFiltro, setCodparcInelegivelFiltro] = useState('');
    const [parceiroCupomModal, setParceiroCupomModal] = useState(null);
    const [criandoCupom, setCriandoCupom] = useState(false);
    const [erroCriacaoCupom, setErroCriacaoCupom] = useState('');
    const [aguardandoConsultaAba, setAguardandoConsultaAba] = useState(false);

    const status = statusByIndex[activeIndex] || 'ATIVO';
    const isAbaInelegiveis = status === 'INELEGIVEL';
    const isAbaElegiveis = status === 'ELEGIVEL';
    const statusLabel = statusLabels[status] || 'Ativos';
    const isTi = !codvendUsuario;
    const podeVerTodos = isTi || tipvend === 'G';
    const possuiPermissao = menu.filter(m => m.menuid === 40 && m.status === 1).length > 0;
    const cuponsStatusAtual = cuponsByStatus?.[status] || [];
    const loadingStatusAtual = !!loadingByStatus?.[status];
    const ofertaCupomModal = getCupomOfertaParceiro(parceiroCupomModal);

    useEffect(() => {
        if (menu.length === 0) {
            history.push('/');
            return;
        }

        if (!possuiPermissao) {
            history.push('/Home');
        }
    }, [menu, possuiPermissao, history]);

    useEffect(() => {
        if (!possuiPermissao) {
            return;
        }

        CupomGerenciadorVendedoresListAction(userid).finally(() => {
            if (codvendUsuario && !podeVerTodos) {
                setVendedorSelecionado(Number(codvendUsuario));
            }
        }).catch(() => { });
    }, [userid, codvendUsuario, podeVerTodos, possuiPermissao, CupomGerenciadorVendedoresListAction]);

    const getPayloadConsulta = useCallback(() => ({
        status,
        codvend: vendedorSelecionado || null,
        codparc: status === 'INELEGIVEL' ? codparcInelegivelFiltro || null : codparcFiltro || null,
        codvendUsuario: codvendUsuario || null,
        tipvend: tipvend || null,
        userid
    }), [status, vendedorSelecionado, codparcFiltro, codparcInelegivelFiltro, codvendUsuario, tipvend, userid]);

    useEffect(() => {
        if (!possuiPermissao || (isAbaInelegiveis && !codparcInelegivelFiltro)) {
            setAguardandoConsultaAba(false);
            return;
        }

        setAguardandoConsultaAba(true);
        CupomGerenciadorListAction(getPayloadConsulta())
            .finally(() => setAguardandoConsultaAba(false))
            .catch(() => { });
    }, [getPayloadConsulta, possuiPermissao, isAbaInelegiveis, codparcInelegivelFiltro, CupomGerenciadorListAction]);

    const vendedorOptions = useMemo(() => {
        const options = vendedores.map((item) => ({
            key: item.codvend,
            text: `${item.apelido || 'Vendedor'} / (${item.codvend})`,
            value: item.codvend
        }));

        return podeVerTodos
            ? [{ key: 'todos', text: 'Todos', value: '' }, ...options]
            : options;
    }, [vendedores, podeVerTodos]);

    const aplicarCodParc = () => {
        if (isAbaInelegiveis) {
            return;
        }

        if (codparcInput === codparcFiltro) {
            setAguardandoConsultaAba(true);
            CupomGerenciadorListAction(getPayloadConsulta())
                .finally(() => setAguardandoConsultaAba(false))
                .catch(() => { });
            return;
        }

        setAguardandoConsultaAba(true);
        setCodparcFiltro(codparcInput);
    };

    const aplicarCodParcInelegivel = () => {
        if (!codparcInelegivel) {
            setCodparcInelegivelFiltro('');
            return;
        }

        if (codparcInelegivel === codparcInelegivelFiltro) {
            return;
        }

        setAguardandoConsultaAba(true);
        setCodparcInelegivelFiltro(codparcInelegivel);
    };

    const recarregarCodParcInelegivel = () => {
        if (!codparcInelegivel) {
            setCodparcInelegivelFiltro('');
            return;
        }

        if (codparcInelegivel === codparcInelegivelFiltro) {
            setAguardandoConsultaAba(true);
            CupomGerenciadorListAction({
                ...getPayloadConsulta(),
                status: 'INELEGIVEL',
                codparc: codparcInelegivel
            }).finally(() => setAguardandoConsultaAba(false)).catch(() => { });
            return;
        }

        aplicarCodParcInelegivel();
    };

    const abrirContatosParceiro = (codparc) => {
        if (!codparc) {
            return;
        }

        history.push(`/Whatsapp?codparc=${codparc}&returnTo=${encodeURIComponent('/CupomGerenciador')}`);
    };

    const renderActionCell = (item, tableStatus) => {
        if (tableStatus !== 'ATIVO') {
            return '';
        }

        const statusWhatsapp = String(item.status_whatsapp || '').toUpperCase();

        if (statusWhatsapp === 'BLOQUEADO') {
            return (
                <Popup
                    content="O cupom será liberado após revisar o cadastro de contatos do parceiro"
                    trigger={(
                        <Button
                            type="button"
                            icon="user times"
                            size="mini"
                            className="cupomActionBtn cupomActionBlocked"
                            onClick={() => abrirContatosParceiro(item.codparc)}
                        />
                    )}
                />
            );
        }

        if (statusWhatsapp === 'LIBERADO') {
            return (
                <span className="cupomOkStatus" title="Cadastro de contatos ok">
                    <Icon name="check circle" />
                    OK
                </span>
            );
        }

        return '';
    };

    const fecharModalCriacao = () => {
        if (criandoCupom) {
            return;
        }

        setErroCriacaoCupom('');
        setParceiroCupomModal(null);
    };

    const confirmarCriacaoCupom = () => {
        if (!parceiroCupomModal || criandoCupom) {
            return;
        }

        const codparc = getFieldValue(parceiroCupomModal, 'codparc');
        const nomeparc = getFieldValue(parceiroCupomModal, 'nomeparc') || 'Parceiro';

        if (!codparc) {
            setErroCriacaoCupom('Não foi possível identificar o código do parceiro.');
            return;
        }

        setCriandoCupom(true);
        setErroCriacaoCupom('');

        InsertCupomDescontoAction(codparc, nomeparc, {
            opcaoDesconto: ofertaCupomModal.opcao
        }).then(() => {
            setParceiroCupomModal(null);
            CupomGerenciadorListAction(getPayloadConsulta()).catch(() => { });
        }).catch(() => {
            setErroCriacaoCupom('Não foi possível criar o cupom. Tente novamente.');
        }).finally(() => {
            setCriandoCupom(false);
        });
    };

    const renderElegivelActionCell = (item) => (
        <Button
            type="button"
            size="mini"
            className="cupomCreateBtn"
            onClick={() => {
                setErroCriacaoCupom('');
                setParceiroCupomModal(item);
            }}
        >
            Criar cupom
        </Button>
    );

    const renderSkeleton = (tableColumns) => (
        <>
            {[0, 1, 2, 3, 4, 5].map((row) => (
                <Table.Row key={`skeleton-${row}`}>
                    {tableColumns.map((column, index) => (
                        <Table.Cell
                            key={column.key}
                            textAlign={column.textAlign}
                            style={{ width: column.width }}
                            className={column.key === 'vendas' ? 'cupomVendasCell' : ''}
                        >
                            <SkeletonBlock width={index % 3 === 0 ? '55px' : index % 3 === 1 ? '120px' : '82px'} />
                        </Table.Cell>
                    ))}
                </Table.Row>
            ))}
        </>
    );

    const renderElegivelCriteriaTooltip = () => (
        <div className="cupomElegivelTooltipContent">
            <div className="cupomElegivelTooltipHeader">
                <Icon name="check circle" />
                <div>
                    <strong>Critérios para cupom</strong>
                    <span>Condições usadas para liberar a geração de cupons.</span>
                </div>
            </div>

            <div className="cupomElegivelTooltipBlock">
                <strong>Critérios para ser elegível</strong>
                <ul>
                    {criteriosCupomBase.map((criterio) => (
                        <li key={criterio}>{criterio}</li>
                    ))}
                </ul>
            </div>

            <div className="cupomElegivelTooltipGrid">
                <div>
                    <strong>Abertura · 19,99%</strong>
                    <ul>
                        {criteriosCupomAbertura.map((criterio) => (
                            <li key={criterio}>{criterio}</li>
                        ))}
                    </ul>
                </div>
                <div>
                    <strong>Reativação · 17,99%</strong>
                    <ul>
                        {criteriosCupomReativacao.map((criterio) => (
                            <li key={criterio}>{criterio}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );

    const renderRows = (tableColumns, tableStatus) => {
        const isElegivelTable = tableStatus === 'ELEGIVEL';
        const isInelegivelTable = tableStatus === 'INELEGIVEL';
        const tableCupons = cuponsByStatus?.[tableStatus] || [];
        const tableLoading = !!loadingByStatus?.[tableStatus];
        const tableErro = erroByStatus?.[tableStatus] || '';
        const emptyTitle = isElegivelTable
            ? 'Nenhum parceiro elegivel encontrado'
            : isInelegivelTable
                ? 'Parceiro não encontrado como inelegível'
                : 'Nenhum cupom encontrado';
        const emptyText = isElegivelTable
            ? 'Ajuste os filtros para consultar outros parceiros elegiveis.'
            : isInelegivelTable
                ? 'Verifique o código informado ou tente consultar outro parceiro.'
                : 'Ajuste os filtros ou troque de aba para consultar outros registros.';

        if (tableLoading || (status === tableStatus && aguardandoConsultaAba)) {
            return renderSkeleton(tableColumns);
        }

        if (tableErro) {
            return (
                <Table.Row>
                    <Table.Cell colSpan={tableColumns.length}>
                        <Message warning icon>
                            <Icon name="warning sign" />
                            <Message.Content>
                                <Message.Header>Falha ao carregar</Message.Header>
                                {tableErro}
                            </Message.Content>
                        </Message>
                    </Table.Cell>
                </Table.Row>
            );
        }

        if (tableCupons.length === 0) {
            return (
                <Table.Row>
                    <Table.Cell colSpan={tableColumns.length} textAlign="center">
                        <div className="emptyState">
                            <Icon name={isElegivelTable ? 'address card outline' : isInelegivelTable ? 'ban' : 'ticket'} />
                            <strong>{emptyTitle}</strong>
                            <span>{emptyText}</span>
                        </div>
                    </Table.Cell>
                </Table.Row>
            );
        }

        return tableCupons.map((item, index) => (
            <Table.Row key={`${tableStatus}-${getFieldValue(item, 'cupomid') || 'elegivel'}-${getFieldValue(item, 'codparc') || index}`}>
                {tableColumns.map((column) => (
                    <Table.Cell
                        key={column.key}
                        textAlign={column.textAlign}
                        style={{ width: column.width }}
                        className={column.key === 'vendas' ? 'cupomVendasCell' : ''}
                    >
                        {column.key === 'acoes'
                            ? renderActionCell(item, tableStatus)
                            : column.key === 'acoes_elegivel'
                                ? renderElegivelActionCell(item)
                                : formatValue(column.key, getFieldValue(item, column.key), item)}
                    </Table.Cell>
                ))}
            </Table.Row>
        ));
    };

    const renderTable = (tableColumns, tableStatus) => (
        <TableWrap>
            <StyledTable celled compact="very" size="small" className={tableStatus === 'ELEGIVEL' || tableStatus === 'INELEGIVEL' ? 'elegivelTable' : ''}>
                <Table.Header>
                    <Table.Row>
                        {tableColumns.map((column) => (
                            <Table.HeaderCell key={column.key} style={{ width: column.width }}>{column.label}</Table.HeaderCell>
                        ))}
                    </Table.Row>
                </Table.Header>
                <Table.Body>{renderRows(tableColumns, tableStatus)}</Table.Body>
            </StyledTable>
        </TableWrap>
    );

    const renderInelegivelDetalhes = () => {
        const inelegiveis = cuponsByStatus?.INELEGIVEL || [];
        const parceiro = inelegiveis[0];
        const loadingInelegivel = !!loadingByStatus?.INELEGIVEL || aguardandoConsultaAba;
        const erroInelegivel = erroByStatus?.INELEGIVEL || '';
        const motivosDoParceiro = getMotivosInelegivel(parceiro);
        const detalhes = parceiro
            ? [
                { label: 'Parceiro', value: formatValue('parceiro_elegivel', null, parceiro), featured: true },
                { label: 'Supervisor', value: formatValue('supervisor', getFieldValue(parceiro, 'supervisor'), parceiro) },
                { label: 'Vendedor', value: formatValue('vendedor', getFieldValue(parceiro, 'vendedor'), parceiro) },
                { label: 'Última visita', value: formatValue('dtultvisita', getFieldValue(parceiro, 'dtultvisita'), parceiro) },
                { label: 'Compra concorrente', value: formatValue('compraconcorrente', getFieldValue(parceiro, 'compraconcorrente'), parceiro) },
                { label: 'Revenda?', value: formatValue('classificicms', getFieldValue(parceiro, 'classificicms'), parceiro) },
                { label: 'Tabela', value: formatValue('codtab', getFieldValue(parceiro, 'codtab'), parceiro) },
                { label: 'Teve cupom', value: formatValue('teve_cupom', getFieldValue(parceiro, 'teve_cupom'), parceiro) },
                { label: 'Última compra', value: formatValue('ultima_compra', getFieldValue(parceiro, 'ultima_compra'), parceiro) },
                { label: '3 meses sem comprar', value: formatValue('3_meses_sem_comprar', getFieldValue(parceiro, '3_meses_sem_comprar'), parceiro) },
                { label: '+10k em 4 anos?', value: formatValue('comprou_mais_10k_4anos', getFieldValue(parceiro, 'comprou_mais_10k_4anos'), parceiro) },
                { label: 'Variação anual', value: formatValue('pct_media_anual', getFieldValue(parceiro, 'pct_media_anual'), parceiro) },
                { label: 'Caiu na média?', value: formatValue('queda_media_anual', getFieldValue(parceiro, 'queda_media_anual'), parceiro) },
                { label: 'Queda >= 50%?', value: formatValue('queda_media_anual_50', getFieldValue(parceiro, 'queda_media_anual_50'), parceiro) }
            ]
            : [];

        if (!codparcInelegivelFiltro) {
            return null;
        }

        if (loadingInelegivel) {
            return (
                <div className="inelegivelDetailCard">
                    <div className="inelegivelDetailHeader">
                        <div>
                            <SkeletonBlock width="220px" />
                            <SkeletonBlock width="140px" />
                        </div>
                        <SkeletonBlock width="86px" />
                    </div>
                    <div className="inelegivelDetailGrid">
                        {[0, 1, 2, 3, 4, 5].map((item) => (
                            <div className="inelegivelDetailItem" key={`inelegivel-loading-${item}`}>
                                <SkeletonBlock width="70px" />
                                <SkeletonBlock width="120px" />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (erroInelegivel) {
            return (
                <Message warning icon className="inelegivelFeedback">
                    <Icon name="warning sign" />
                    <Message.Content>
                        <Message.Header>Falha ao consultar parceiro</Message.Header>
                        {erroInelegivel}
                    </Message.Content>
                </Message>
            );
        }

        if (!parceiro) {
            return (
                <div className="inelegivelEmptyCard">
                    <Icon name="check circle outline" />
                    <strong>Parceiro não encontrado como inelegível</strong>
                    <span>Verifique o código informado ou tente consultar outro parceiro.</span>
                </div>
            );
        }

        return (
            <div className="inelegivelDetailCard">
                <div className="inelegivelDetailHeader">
                    <div className="inelegivelDetailTitle">
                        <Icon name="warning sign" />
                        <div>
                            <strong>Parceiro inelegível para cupom.</strong>
                            <span>Confira os dados encontrados e os motivos que impediram a geração.</span>
                        </div>
                    </div>
                    <Label basic color="grey">Inelegível</Label>
                </div>

                <div className="inelegivelDetailGrid">
                    {detalhes.map((detalhe) => (
                        <div className={detalhe.featured ? 'inelegivelDetailItem featured' : 'inelegivelDetailItem'} key={detalhe.label}>
                            <span>{detalhe.label}</span>
                            <strong>{detalhe.value === null || detalhe.value === undefined || detalhe.value === '' ? '-' : detalhe.value}</strong>
                        </div>
                    ))}
                </div>

                <div className="inelegivelReasons">
                    <div>
                        <Icon name="info circle" />
                        <strong>Motivos que tornaram o parceiro inelegível</strong>
                    </div>
                    <ul>
                        {motivosDoParceiro.map((motivo) => (
                            <li key={motivo}>{motivo}</li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    };

    const renderInelegiveisPlaceholder = () => (
        <div className="inelegiveisPanel">
            <div className="inelegiveisSearch">
                <div className="inelegiveisText">
                    <Icon name="search" />
                    <div>
                        <strong>Verificar parceiro inelegível</strong>
                        <span>Informe um código de parceiro para preparar a consulta de inelegibilidade.</span>
                    </div>
                </div>

                <Input
                    icon="id card outline"
                    value={codparcInelegivel}
                    placeholder="Código do parceiro"
                    onChange={(e) => setCodparcInelegivel(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            recarregarCodParcInelegivel();
                        }
                    }}
                    onBlur={aplicarCodParcInelegivel}
                />
            </div>
            {codparcInelegivelFiltro && (
                <div className="inelegiveisResult">
                    {renderInelegivelDetalhes()}
                </div>
            )}
        </div>
    );

    const tabs = [
        {
            key: 'ativos',
            icon: 'check circle outline',
            label: 'Ativos',
            status: 'ATIVO'
        },
        {
            key: 'inativos',
            icon: 'pause circle outline',
            label: 'Inativos',
            status: 'INATIVO'
        },
        {
            key: 'elegiveis',
            icon: 'address card outline',
            label: 'Elegíveis',
            status: 'ELEGIVEL'
        },
        {
            key: 'inelegiveis',
            icon: 'ban',
            label: 'Inelegíveis',
            status: 'INELEGIVEL'
        }
    ];

    const alterarAba = (index) => {
        const nextStatus = statusByIndex[index] || 'ATIVO';
        setAguardandoConsultaAba(nextStatus !== 'INELEGIVEL');
        setActiveIndex(index);
    };

    const renderActiveTabContent = () => {
        if (status === 'INELEGIVEL') {
            return renderInelegiveisPlaceholder();
        }

        if (status === 'ELEGIVEL') {
            return renderTable(elegivelColumns, 'ELEGIVEL');
        }

        return renderTable(status === 'ATIVO' ? cupomAtivoColumns : cupomColumns, status);
    };

    if (!possuiPermissao) {
        return null;
    }

    return (
        <Page>
            <Menu stackable size="mini" className="cupomTopMenu">
                <Menu.Item>
                    <Breadcrumb size="mini">
                        <Breadcrumb.Section href="/Home">Home</Breadcrumb.Section>
                        <Breadcrumb.Divider icon="right chevron" />
                        <Breadcrumb.Section active>Cupons</Breadcrumb.Section>
                    </Breadcrumb>
                </Menu.Item>
            </Menu>

            <Toolbar>
                <FilterGroup>
                    <FilterField>
                        Vendedor
                        <Dropdown
                            search
                            selection
                            clearable={isTi}
                            options={vendedorOptions}
                            value={vendedorSelecionado}
                            onChange={(e, { value }) => {
                                if (!isTi && (value === '' || value === null || value === undefined)) {
                                    return;
                                }

                                setVendedorSelecionado(value || '');
                            }}
                            placeholder="Selecione"
                            loading={vendedoresLoading}
                            disabled={vendedoresLoading}
                        />
                    </FilterField>

                    <CodParcField>
                        Código do Parceiro
                        <Input
                            icon="search"
                            value={codparcInput}
                            placeholder="Código"
                            onChange={(e) => setCodparcInput(e.target.value.replace(/\D/g, ''))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    aplicarCodParc();
                                }
                            }}
                            onBlur={aplicarCodParc}
                        />
                    </CodParcField>

                    <Button
                        icon
                        labelPosition="left"
                        color="blue"
                        type="button"
                        onClick={aplicarCodParc}
                        loading={loadingStatusAtual || aguardandoConsultaAba}
                        disabled={loadingStatusAtual || aguardandoConsultaAba}
                    >
                        <Icon name="search" />
                        Buscar
                    </Button>
                </FilterGroup>

                <Summary>
                    <span className="cupomSummaryCriteriaSlot">
                        {isAbaElegiveis && (
                            <Popup
                                trigger={(
                                    <span className="cupomCriteriaHint">
                                        <Icon name="info circle" />
                                        Critérios para cupom
                                    </span>
                                )}
                                className="cupomElegivelTooltip"
                                content={renderElegivelCriteriaTooltip()}
                                position="bottom center"
                                popper={{ strategy: 'fixed' }}
                                mouseEnterDelay={180}
                                on="hover"
                                hoverable
                            />
                        )}
                    </span>
                    <span className="cupomSummaryStatus">
                        <span className="cupomSummaryCount">
                            <Icon name={isAbaElegiveis ? 'address card outline' : isAbaInelegiveis ? 'ban' : 'ticket'} color={activeIndex === 0 || isAbaElegiveis ? 'green' : 'grey'} />
                            {loadingStatusAtual || aguardandoConsultaAba ? 'Carregando' : `${cuponsStatusAtual.length} registro(s)`}
                        </span>
                        <Label color={activeIndex === 0 || isAbaElegiveis ? 'green' : 'grey'}>{statusLabel}</Label>
                    </span>
                </Summary>
            </Toolbar>

            <Grid columns={1} doubling>
                <Grid.Column>
                    <Menu secondary pointing className="cupomTabs">
                        {tabs.map((tab, index) => (
                            <Menu.Item
                                key={tab.key}
                                active={activeIndex === index}
                                onClick={() => alterarAba(index)}
                            >
                                <Icon name={tab.icon} />
                                {tab.label}
                            </Menu.Item>
                        ))}
                    </Menu>
                    <div className="cupomTabPanel" key={status}>
                        {renderActiveTabContent()}
                    </div>
                </Grid.Column>
            </Grid>

            <Modal
                size="mini"
                open={!!parceiroCupomModal}
                onClose={fecharModalCriacao}
                closeOnDimmerClick={!criandoCupom}
                className="cupomCreateModal"
            >
                <Modal.Header>
                    <Icon name="ticket" />
                    Criar cupom
                </Modal.Header>
                <Modal.Content>
                    <p>
                        Tem certeza de que deseja criar um cupom para este parceiro?
                    </p>
                    <div className="cupomCreateModalPartner">
                        <strong>{getFieldValue(parceiroCupomModal, 'codparc') || '-'}</strong>
                        <span>{getFieldValue(parceiroCupomModal, 'nomeparc') || 'Parceiro sem nome informado'}</span>
                    </div>
                    <div className="cupomCreateModalOffer">
                        <div>
                            <span>Tipo de cupom</span>
                            <strong>{ofertaCupomModal.tipo}</strong>
                        </div>
                        <div>
                            <span>Desconto</span>
                            <strong>{ofertaCupomModal.desconto}</strong>
                        </div>
                    </div>
                    {erroCriacaoCupom && (
                        <Message warning icon className="cupomCreateModalError">
                            <Icon name="warning sign" />
                            <Message.Content>{erroCriacaoCupom}</Message.Content>
                        </Message>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button type="button" basic onClick={fecharModalCriacao} disabled={criandoCupom}>
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        className="cupomCreateConfirmBtn"
                        onClick={confirmarCriacaoCupom}
                        loading={criandoCupom}
                        disabled={criandoCupom}
                    >
                        Criar
                    </Button>
                </Modal.Actions>
            </Modal>

            <style>{`
                .cupomTopMenu.ui.menu {
                    background: transparent;
                    border: none;
                    border-bottom: solid 1.5px #1884af;
                    box-shadow: none;
                    border-radius: 0 0 5px 5px;
                }

                .cupomTopMenu.ui.mini.menu > .item {
                    padding: 0;
                }

                .cupomTopMenu .ui.breadcrumb .section {
                    padding: 1.5em 1.5em !important;
                }

                .cupomTopMenu .ui.breadcrumb .section:first-child {
                    padding-right: 0 !important;
                    border-radius: 0 0 0 5px !important;
                }

                .cupomTopMenu .ui.breadcrumb .active.section {
                    background-color: #1884af !important;
                    color: #fff !important;
                }

                .emptyState {
                    min-height: 150px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: .35em;
                    color: #5f6f7a;
                }

                .emptyState > i.icon {
                    width: 46px;
                    height: 46px;
                    line-height: 46px;
                    margin: 0 0 .35em 0 !important;
                    border-radius: 50%;
                    background: #fff6d8;
                    color: #b58200;
                    font-size: 1.35em;
                }

                .emptyState > strong {
                    color: #24343d;
                    font-size: 1.05em;
                }

                .ui.button.cupomActionBtn {
                    width: 28px;
                    height: 28px;
                    min-height: 28px;
                    padding: 0 !important;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                }

                .ui.button.cupomActionBtn > i.icon {
                    margin: 0 !important;
                }

                .ui.button.cupomActionBlocked,
                .ui.button.cupomActionBlocked:focus {
                    position: relative;
                    background: linear-gradient(135deg, #b70f1a 0%, #e0313e 52%, #ff6f78 100%) !important;
                    color: #fff !important;
                    border: 1px solid #ffb0b7 !important;
                    box-shadow: 0 0 0 0 rgba(224, 49, 62, .55);
                    animation: cupomBlockedPulse 1.45s ease-in-out infinite;
                }

                .ui.button.cupomActionBlocked:hover {
                    background: linear-gradient(135deg, #9f0c15 0%, #cf1f2d 50%, #f85c66 100%) !important;
                    color: #fff !important;
                }

                @keyframes cupomBlockedPulse {
                    0% {
                        box-shadow: 0 0 0 0 rgba(224, 49, 62, .55);
                    }
                    70% {
                        box-shadow: 0 0 0 7px rgba(224, 49, 62, 0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(224, 49, 62, 0);
                    }
                }

                .cupomOkStatus {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: .25em;
                    color: #20853b;
                    font-size: .84em;
                    font-weight: 700;
                    white-space: nowrap;
                }

                .cupomOkStatus > i.icon {
                    margin: 0 !important;
                }

                .cupomTabs.ui.secondary.pointing.menu {
                    margin-bottom: 1em;
                }

                .cupomTabPanel {
                    width: 100%;
                }

                .ui.table td.cupomVendasCell {
                    white-space: nowrap;
                    overflow-wrap: normal;
                    word-break: keep-all;
                }

                .cupomSummaryCriteriaSlot {
                    flex: 1 1 auto;
                    display: flex;
                    justify-content: center;
                    min-width: 0;
                }

                .cupomSummaryStatus {
                    display: inline-flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: .5em;
                    flex: 0 0 auto;
                }

                .cupomSummaryCount,
                .cupomCriteriaHint {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: .35em;
                    min-height: 22px;
                    line-height: 1;
                    white-space: nowrap;
                }

                .cupomSummaryCount {
                    color: #48616b;
                    font-size: .9em;
                    font-weight: 800;
                }

                .cupomCriteriaHint {
                    color: #1884af;
                    font-size: .9em;
                    font-weight: 800;
                    cursor: help;
                }

                .cupomSummaryCount > i.icon,
                .cupomCriteriaHint > i.icon {
                    margin: 0 !important;
                    line-height: 1 !important;
                }

                .cupomCriteriaHint > i.icon {
                    color: #0f766e;
                }

                .cupomCriteriaHint:hover {
                    color: #0f6f95;
                    text-decoration: underline;
                    text-underline-offset: 2px;
                }

                .ui.table.elegivelTable {
                    min-width: 0;
                    width: 100%;
                    table-layout: fixed;
                    font-size: .92em;
                }

                .ui.table.elegivelTable tbody tr {
                    background: #fff;
                }

                .ui.table.elegivelTable thead th {
                    padding: .45em .18em !important;
                    font-size: .68em;
                    line-height: 1.08;
                    word-break: normal;
                    overflow-wrap: anywhere;
                }

                .ui.table.elegivelTable tbody td {
                    padding: .38em .18em !important;
                    font-size: .7em;
                    line-height: 1.12;
                    word-break: normal;
                    overflow-wrap: anywhere;
                }

                .ui.table.elegivelTable .ui.button.cupomCreateBtn {
                    min-width: 0;
                    width: 100%;
                    min-height: 28px;
                    padding: .48em .35em .48em .8em !important;
                    font-size: .86em;
                }

                .ui.table.elegivelTable .ui.button.cupomCreateBtn:before {
                    left: .42em;
                    width: 4px;
                    height: 4px;
                }

                .cupomElegivelTooltip.ui.popup {
                    max-width: 430px;
                    border: 1px solid #d7e8ef;
                    border-radius: 8px;
                    background: #ffffff;
                    color: #334850;
                    box-shadow: 0 14px 34px rgba(36, 66, 79, .18);
                }

                .cupomElegivelTooltipContent {
                    display: flex;
                    flex-direction: column;
                    gap: .7em;
                    font-size: .88em;
                    line-height: 1.25;
                }

                .cupomElegivelTooltipHeader {
                    display: flex;
                    align-items: flex-start;
                    gap: .6em;
                    padding-bottom: .65em;
                    border-bottom: 1px solid #edf3f5;
                }

                .cupomElegivelTooltipHeader > i.icon {
                    width: 30px;
                    height: 30px;
                    line-height: 30px;
                    margin: 0 !important;
                    border-radius: 8px;
                    background: #e8f7f1;
                    color: #0f766e;
                    text-align: center;
                }

                .cupomElegivelTooltipHeader strong,
                .cupomElegivelTooltipHeader span {
                    display: block;
                }

                .cupomElegivelTooltipHeader strong {
                    color: #24343d;
                    font-size: 1.02em;
                }

                .cupomElegivelTooltipHeader span {
                    margin-top: .1em;
                    color: #60727a;
                    font-size: .88em;
                }

                .cupomElegivelTooltipBlock,
                .cupomElegivelTooltipGrid > div {
                    padding: .65em;
                    border: 1px solid #edf3f5;
                    border-radius: 8px;
                    background: #fbfcfd;
                }

                .cupomElegivelTooltipBlock > strong,
                .cupomElegivelTooltipGrid strong {
                    display: block;
                    margin-bottom: .35em;
                    color: #1884af;
                    font-size: .9em;
                }

                .cupomElegivelTooltipGrid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: .6em;
                }

                .cupomElegivelTooltipContent ul {
                    margin: 0;
                    padding-left: 1.1em;
                }

                .cupomElegivelTooltipContent li {
                    margin: .18em 0;
                    color: #43545c;
                }

                .ui.button.cupomCreateBtn,
                .ui.button.cupomCreateBtn:disabled {
                    position: relative;
                    min-width: 94px;
                    min-height: 32px;
                    padding: .58em 1em .58em 1.35em !important;
                    border-radius: 8px;
                    background: #e9e3fc !important;
                    color: #7047eb !important;
                    font-weight: 700;
                    cursor: pointer !important;
                    letter-spacing: 0;
                    box-shadow: 0 4px 10px rgba(103, 71, 232, .18);
                    opacity: .9 !important;
                    transition: background-color .14s ease, box-shadow .14s ease, border-color .14s ease;
                }

                .ui.button.cupomCreateBtn:before {
                    content: '';
                    position: absolute;
                    left: .72em;
                    top: 50%;
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: #22c55e;
                    transform: translateY(-50%);
                }

                .ui.button.cupomCreateBtn:hover {
                    background: #6b4ced !important;
                    border-color: #5f43d9 !important;
                    color: #e9e3fc !important;
                    box-shadow: 0 5px 12px rgba(103, 71, 232, .18);
                }

                .ui.modal.cupomCreateModal {
                    border-radius: 8px;
                    overflow: hidden;
                }

                .ui.modal.cupomCreateModal > .header {
                    display: flex;
                    align-items: center;
                    gap: .5em;
                    color: #24343d;
                    border-bottom: 1px solid #d7e8ef;
                }

                .ui.modal.cupomCreateModal > .header > i.icon {
                    color: #6747e8;
                    margin: 0 !important;
                }

                .cupomCreateModalPartner {
                    display: flex;
                    flex-direction: column;
                    gap: .2em;
                    margin-top: 1em;
                    padding: .85em;
                    border: 1px solid #e6edf1;
                    border-radius: 8px;
                    background: #f8fbfc;
                }

                .cupomCreateModalPartner strong {
                    color: #1884af;
                    font-size: 1.05em;
                }

                .cupomCreateModalPartner span {
                    color: #43545c;
                    font-weight: 600;
                }

                .cupomCreateModalOffer {
                    display: grid;
                    grid-template-columns: 1fr 110px;
                    gap: .75em;
                    margin-top: .75em;
                }

                .cupomCreateModalOffer > div {
                    display: flex;
                    flex-direction: column;
                    gap: .15em;
                    padding: .85em;
                    border: 1px solid #ece7ff;
                    border-radius: 8px;
                    background: #fbfaff;
                }

                .cupomCreateModalOffer span {
                    color: #6b7280;
                    font-size: .78em;
                    font-weight: 700;
                    text-transform: uppercase;
                }

                .cupomCreateModalOffer strong {
                    color: #4f35c5;
                    font-size: 1.02em;
                }

                .cupomCreateModalOffer > div:last-child {
                    align-items: center;
                    justify-content: center;
                    background: #f4f0ff;
                }

                .cupomCreateModalOffer > div:last-child strong {
                    font-size: 1.22em;
                }

                .cupomCreateModalError {
                    margin-top: .85em !important;
                }

                .ui.button.cupomCreateConfirmBtn {
                    background: #6747e8 !important;
                    color: #fff !important;
                    border-radius: 6px;
                    font-weight: 700;
                }

                .ui.button.cupomCreateConfirmBtn:hover {
                    background: #5f3fd8 !important;
                    color: #fff !important;
                }

                .inelegiveisPanel {
                    min-height: 240px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1em;
                    padding: 2em 1em;
                    border: 1px solid #d7e8ef;
                    border-radius: 8px;
                    background: linear-gradient(135deg, #f8fcfb 0%, #eef8f5 100%);
                }

                .inelegiveisSearch {
                    width: min(620px, 100%);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1em;
                    padding: 1em;
                    border: 1px solid rgba(24, 132, 175, .22);
                    border-radius: 8px;
                    background: #fff;
                    box-shadow: 0 12px 32px rgba(20, 86, 109, .12);
                }

                .inelegiveisText {
                    display: flex;
                    align-items: center;
                    gap: .75em;
                    color: #29424c;
                }

                .inelegiveisText > i.icon {
                    width: 40px;
                    height: 40px;
                    line-height: 40px;
                    margin: 0 !important;
                    border-radius: 8px;
                    background: #e8f7f1;
                    color: #0f766e;
                    text-align: center;
                }

                .inelegiveisText strong,
                .inelegiveisText span {
                    display: block;
                }

                .inelegiveisText span {
                    margin-top: .2em;
                    color: #60727a;
                    font-size: .88em;
                }

                .inelegiveisSearch .ui.input {
                    min-width: 190px;
                }

                .inelegiveisResult {
                    width: 100%;
                }

                .inelegivelDetailCard,
                .inelegivelEmptyCard {
                    width: min(980px, 100%);
                    margin: 0 auto;
                    border: 1px solid rgba(217, 119, 6, .22);
                    border-radius: 8px;
                    background: #fff;
                    box-shadow: 0 14px 34px rgba(55, 65, 81, .12);
                }

                .inelegivelDetailCard {
                    padding: 1em;
                }

                .inelegivelDetailHeader {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 1em;
                    padding-bottom: .9em;
                    border-bottom: 1px solid #eef2f5;
                }

                .inelegivelDetailTitle {
                    display: flex;
                    align-items: flex-start;
                    gap: .75em;
                    color: #24343d;
                }

                .inelegivelDetailTitle > i.icon {
                    width: 38px;
                    height: 38px;
                    line-height: 38px;
                    margin: 0 !important;
                    border-radius: 8px;
                    background: #fff4de;
                    color: #c06a00;
                    text-align: center;
                    box-shadow: inset 0 0 0 1px rgba(217, 119, 6, .18);
                }

                .inelegivelDetailTitle strong,
                .inelegivelDetailTitle span {
                    display: block;
                }

                .inelegivelDetailTitle strong {
                    color: #9a5200;
                    font-size: 1.05em;
                }

                .inelegivelDetailTitle span {
                    margin-top: .2em;
                    color: #60727a;
                    font-size: .9em;
                }

                .inelegivelDetailHeader .ui.label {
                    margin: 0;
                    border-radius: 999px;
                    background: #f3f4f6 !important;
                    color: #4b5563 !important;
                    font-weight: 800;
                }

                .inelegivelDetailGrid {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: .75em;
                    margin-top: 1em;
                }

                .inelegivelDetailItem {
                    min-height: 64px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: .25em;
                    padding: .75em;
                    border: 1px solid #edf1f4;
                    border-radius: 8px;
                    background: #fbfcfd;
                }

                .inelegivelDetailItem.featured {
                    grid-column: span 2;
                    background: #f8fbfc;
                    border-color: #dbeaf0;
                }

                .inelegivelDetailItem span {
                    color: #6b7b83;
                    font-size: .76em;
                    font-weight: 800;
                    text-transform: uppercase;
                }

                .inelegivelDetailItem strong {
                    color: #24343d;
                    font-size: .96em;
                    line-height: 1.25;
                    overflow-wrap: anywhere;
                }

                .inelegivelReasons {
                    margin-top: 1em;
                    padding: .9em;
                    border: 1px solid #fde4b7;
                    border-radius: 8px;
                    background: #fffaf0;
                }

                .inelegivelReasons > div {
                    display: flex;
                    align-items: center;
                    gap: .45em;
                    color: #9a5200;
                    font-weight: 800;
                    line-height: 1;
                }

                .inelegivelReasons > div > i.icon {
                    margin: 0 !important;
                    color: #d97706;
                    line-height: 1 !important;
                }

                .inelegivelReasons ul {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: .45em .9em;
                    margin: .75em 0 0;
                    padding-left: 1.2em;
                }

                .inelegivelReasons li {
                    color: #4b5563;
                    line-height: 1.35;
                }

                .inelegivelEmptyCard {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: .35em;
                    padding: 1.35em;
                    text-align: center;
                    border-color: #d7e8ef;
                }

                .inelegivelEmptyCard > i.icon {
                    margin: 0 !important;
                    color: #0f766e;
                    font-size: 1.8em;
                }

                .inelegivelEmptyCard strong {
                    color: #24343d;
                    font-size: 1.02em;
                }

                .inelegivelEmptyCard span {
                    color: #60727a;
                }

                .inelegivelFeedback {
                    width: min(980px, 100%);
                    margin: 0 auto !important;
                }

                @media (max-width: 720px) {
                    .inelegiveisSearch {
                        align-items: stretch;
                        flex-direction: column;
                    }

                    .inelegiveisSearch .ui.input {
                        min-width: 0;
                        width: 100%;
                    }

                    .inelegivelDetailHeader {
                        flex-direction: column;
                    }

                    .inelegivelDetailGrid,
                    .inelegivelReasons ul {
                        grid-template-columns: 1fr;
                    }

                    .inelegivelDetailItem.featured {
                        grid-column: span 1;
                    }
                }
            `}</style>
        </Page>
    );
}

const mapStateToProps = (state) => ({
    userid: state.LoginReducer.userid,
    codvendUsuario: state.LoginReducer.vendedorvcc,
    tipvend: state.LoginReducer.tipvend,
    menu: state.LoginReducer.menu,
    vendedores: state.CupomReducer.cupomGerenciadorVendedoresList,
    vendedoresLoading: state.CupomReducer.cupomGerenciadorVendedoresLoader,
    cuponsByStatus: state.CupomReducer.cupomGerenciadorListByStatus,
    loadingByStatus: state.CupomReducer.cupomGerenciadorLoaderByStatus,
    erroByStatus: state.CupomReducer.cupomGerenciadorErroByStatus,
});

export default withRouter(connect(mapStateToProps, {
    CupomGerenciadorListAction,
    CupomGerenciadorVendedoresListAction,
    InsertCupomDescontoAction
})(CupomGerenciador));
